import { get } from "svelte/store";
import localForage from "localforage";
import pkg from "lz-string";
import { sleep } from "../../helpers/time.js";
import { demo } from "../../helpers/demo.js";
const { compress, decompress } = pkg;

const PREFIX = "v2:";
const DEBOUNCE_INTERVAL = 5000;
const MAXIMUM_PERSISTENCE_INTERVAL = 10000;

let isResetting = false;
let queue = [];
let isDraining = false;

async function dequeue() {
    if (isDraining) {
        return;
    }

    isDraining = true;

    while (queue.length > 0) {
        const task = queue.shift();

        try {
            await task();
        } catch (error) {
            // Safari can expire an IndexedDB transaction between localforage's calls
            // ("Attempt to delete range from database without an in-progress transaction").
            // Retry once after a pause, then move on, instead of aborting the drain and
            // stranding every queued write behind the failed one.
            console.warn("failed to persist store", error);

            try {
                await sleep(500);
                await task();
            } catch (retryError) {
                console.warn("failed to persist store after retry", retryError);
            }
        }
    }

    isDraining = false;
}

async function withRetry(task) {
    try {
        await task();
    } catch (error) {
        console.warn("storage task failed, retrying", error);
        await sleep(500);
        await task();
    }
}

function beginReset() {
    isResetting = true;
    queue.length = 0;
}

function persisted(key, shouldCompress, store, migrate) {
    if (demo || typeof localStorage === "undefined") {
        return store;
    }

    const storageKey = PREFIX + key;

    let timer;
    let lastPersistedAt = Date.now();
    let shouldSkipNextThis = false;

    async function persist() {
        if (isResetting) {
            return;
        }

        if (shouldSkipNextThis) {
            shouldSkipNextThis = false;
            return;
        }

        queue.push(async () => {
            const json = JSON.stringify(get(store));
            const value = shouldCompress ? compress(json) : json;
            await localForage.setItem(storageKey, value);
        });

        lastPersistedAt = Date.now();

        await dequeue();
    }

    store.subscribe(() => {
        clearTimeout(timer);

        if (Date.now() - lastPersistedAt > MAXIMUM_PERSISTENCE_INTERVAL) {
            persist().catch((error) => {
                console.warn("failed to schedule persistence", error);
            });
        } else {
            timer = setTimeout(persist, DEBOUNCE_INTERVAL);
        }
    });

    function reset(persisted) {
        const json = shouldCompress ? decompress(persisted) : persisted;
        let state;

        try {
            state = JSON.parse(json);
        } catch (error) {
            console.warn("failed to load store", error.message);
        }

        if (migrate) {
            state = migrate(state);
        }

        if (state) {
            store.set(state);
        }
    }

    const persistedLocalStorage = localStorage.getItem(storageKey);

    if (persistedLocalStorage) {
        reset(persistedLocalStorage);
        localStorage.removeItem(storageKey);
    }

    localForage.getItem(storageKey).then((persistedLocalForage) => {
        if (persistedLocalForage) {
            reset(persistedLocalForage);
        }
    }).catch((error) => {
        console.warn("failed to restore store from storage", error);
    });

    return store;
}

async function clearLocalForage() {
    const keys = await localForage.keys();

    for (const key of keys) {
        await localForage.removeItem(key);
    }
}

export async function reset() {
    if (demo) {
        document.location.reload();
        return;
    }
    beginReset();
    localStorage.clear();

    try {
        await withRetry(() => localForage.clear());
    } catch (error) {
        console.warn("failed to clear storage", error);
    }

    document.location.reload();
}

export async function restore(json) {
    if (demo) {
        return;
    }
    beginReset();

    Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(PREFIX)) {
            localStorage.removeItem(key);
        }
    });

    try {
        await withRetry(clearLocalForage);

        for (const [key, value] of Object.entries(json)) {
            await localForage.setItem(key, value);
        }

        document.location.reload();
    } catch (error) {
        console.warn("failed to restore storage", error);
    }
}

export default persisted;
